// What this device remembers between visits, and what it deliberately does not.
//
// The seed is here only inside a vault, sealed under the passcode — and, where a passkey has
// been set up, a second time inside a vault sealed under a key only that passkey produces,
// after it has checked who is holding the device. Neither key is ever written. Everything
// else kept here is public by nature — an account number, a device id, a public key — or
// short-lived, like the session token. The book keys are never written: they are opened from
// their envelopes with the identity key each time the app starts, so a copy of this store is
// worth nothing on its own.
//
// Where it is kept is the platform's business (see platform.ts): localStorage in a browser,
// the keychain and the preferences in a shell. It is read once, before the first screen, into
// the map below — which is why every accessor here is plain and synchronous, and why not one
// of the fifty places that call them had to learn about promises.
import type { KeyVault, Vault } from '@chilavubook/core';
import { platform } from './platform/index.js';
import { dropHeld } from './hold.js';

const VAULT = 'cb.vault';
const PASSKEY = 'cb.passkey';
const DEVICE = 'cb.device';
const ACCOUNT = 'cb.account';
const SESSION = 'cb.session';
const DEFAULT_BOOK = 'cb.default-book';   // what opens first: a book id, or 'all'
const CLAIM = 'cb.claim';                 // a link opened, waiting for its maker to let us in
const LANG = 'cb.lang';                   // the language this device reads in
const REGION = 'cb.region';               // where this device is, for how numbers and dates are written
const CURRENCY = 'cb.currency';           // what a new book or pocket starts in
const ZONE = 'cb.zone';                   // the clock its days begin and end by
const LINK = 'cb.link';                   // an invite link tapped, waiting for a session
const HANDOVER = 'cb.handover';           // a half-finished enrolment, waiting for its box
const LOCK_AFTER = 'cb.lock-after';       // how long the app stays open unused before it locks

const b64 = (b: Uint8Array): string => btoa(String.fromCharCode(...b));
const unb64 = (s: string): Uint8Array => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** Everything read at boot. Empty until `hydrate`, which main.tsx awaits before the app is
 *  even imported — so a module that reads a value while it is being loaded reads the truth. */
let cell = new Map<string, string>();

export async function hydrate(): Promise<void> {
  cell = await platform.loadSecrets();
}

const get = (key: string): string | null => cell.get(key) ?? null;
const put = (key: string, value: string | null): void => {
  if (value === null) cell.delete(key); else cell.set(key, value);
  platform.writeSecret(key, value);
};

export interface DeviceRecord { id: string; pub: string }
export interface SessionRecord { token: string; expires_at: string }

export const savedVault = (): Vault | null => {
  const raw = get(VAULT);
  if (!raw) return null;
  const v = JSON.parse(raw);
  return { ...v, salt: unb64(v.salt), nonce: unb64(v.nonce), cipher: unb64(v.cipher) };
};

export const saveVault = (v: Vault): void => put(VAULT, JSON.stringify({
  ...v, salt: b64(v.salt), nonce: b64(v.nonce), cipher: b64(v.cipher),
}));

/** The passkey this device unlocks with: which credential, the salt its PRF is evaluated
 *  over, and the seed sealed under the key that comes back. */
export interface PasskeyRecord { credentialId: Uint8Array; salt: Uint8Array; vault: KeyVault }

export const savedPasskey = (): PasskeyRecord | null => {
  const raw = get(PASSKEY);
  if (!raw) return null;
  const p = JSON.parse(raw);
  return { credentialId: unb64(p.credential_id), salt: unb64(p.salt),
    vault: { v: 2, nonce: unb64(p.vault.nonce), cipher: unb64(p.vault.cipher) } };
};
export const savePasskey = (p: PasskeyRecord): void => put(PASSKEY, JSON.stringify({
  credential_id: b64(p.credentialId), salt: b64(p.salt),
  vault: { v: 2, nonce: b64(p.vault.nonce), cipher: b64(p.vault.cipher) },
}));
export const forgetPasskey = (): void => put(PASSKEY, null);

export const savedDevice = (): DeviceRecord | null => JSON.parse(get(DEVICE) ?? 'null');
export const saveDevice = (d: DeviceRecord): void => put(DEVICE, JSON.stringify(d));

export const savedAccount = (): string | null => get(ACCOUNT);
export const saveAccount = (n: string): void => put(ACCOUNT, n);

export const savedSession = (): SessionRecord | null => {
  const s = JSON.parse(get(SESSION) ?? 'null') as SessionRecord | null;
  // A token the server would refuse is worse than none: it sends the app down the "logged in"
  // path only to fail on the first call.
  return s && new Date(s.expires_at) > new Date() ? s : null;
};
export const saveSession = (s: SessionRecord): void => put(SESSION, JSON.stringify(s));
/** A token the server has refused, and a fresh challenge could not replace: gone, so the next
 *  start asks again rather than reusing it. */
export const forgetSession = (): void => put(SESSION, null);

/** The view this device opens on: a book id, 'all' for every book at once, or nothing chosen.
 *  A book id is routing metadata the server has anyway; the name stays in the ciphertext. */
export const savedDefaultBook = (): string | null => get(DEFAULT_BOOK);
export const saveDefaultBook = (id: string | null): void => put(DEFAULT_BOOK, id);

/** The language chosen under Account, or nothing for the browser's own. */
export const savedLang = (): string | null => get(LANG);

/** How long the app stays open unused before it locks — and, within that, whether a reload
 *  comes straight back in. `close` is the strictest: nothing held, every new page asks. */
export type LockAfter = '5m' | '15m' | '1h' | 'close';
export const LOCK_AFTER_MS: Record<Exclude<LockAfter, 'close'>, number> = { '5m': 5 * 60_000, '15m': 15 * 60_000, '1h': 60 * 60_000 };
export const savedLockAfter = (): LockAfter => {
  const v = get(LOCK_AFTER);
  return v === '5m' || v === '1h' || v === 'close' ? v : '15m';
};
export const saveLockAfter = (v: LockAfter): void => put(LOCK_AFTER, v);
export const saveLang = (id: string): void => put(LANG, id);

/** Where this device is (ISO 3166), the currency new books start in (ISO 4217) and the zone
 *  its days are counted in (IANA), each chosen under Account — or nothing, for the device's
 *  own. Public by nature, like the language, and kept per device for the same reason. */
export const savedRegion = (): string | null => get(REGION);
export const saveRegion = (v: string | null): void => put(REGION, v);
export const savedCurrency = (): string | null => get(CURRENCY);
export const saveCurrency = (v: string | null): void => put(CURRENCY, v);
export const savedZone = (): string | null => get(ZONE);
export const saveZone = (v: string | null): void => put(ZONE, v);

/** An invite link tapped and not yet claimed: the token, and the keys from after the #. Kept
 *  with the other records rather than for the life of a tab, because a phone may put the app
 *  away between the tap and the unlock and bring it back a fresh page. */
export interface HeldLink { token: string; key: string }
export const savedHeldLink = (): HeldLink | null => JSON.parse(get(LINK) ?? 'null');
export const saveHeldLink = (l: HeldLink): void => put(LINK, JSON.stringify(l));
export const forgetHeldLink = (): void => put(LINK, null);

/** A link this device opened and is waiting on: the claim, and the maker's keys as the link
 *  carried them — the anchor the invite that arrives is checked against. Public keys only. */
export interface ClaimRecord { id: string; signPub: Uint8Array; boxPub: Uint8Array; accountNo: string }
export const savedClaim = (): ClaimRecord | null => {
  const raw = get(CLAIM);
  if (!raw) return null;
  const c = JSON.parse(raw);
  return { id: c.id, signPub: unb64(c.sign_pub), boxPub: unb64(c.box_pub), accountNo: c.account_no };
};
export const saveClaim = (c: ClaimRecord): void => put(CLAIM, JSON.stringify({
  id: c.id, sign_pub: b64(c.signPub), box_pub: b64(c.boxPub), account_no: c.accountNo,
}));
export const forgetClaim = (): void => put(CLAIM, null);

/**
 * An enrolment in flight: the handover this device asked the server for, and the ephemeral
 * key only this device can open its box with.
 *
 * Kept here rather than in a variable because the screen that holds them is a screen you are
 * told to walk away from — to the other device, or to the other app on the same phone. A
 * browser tab survives that; an Android WebView may not, and one that comes back re-mounted
 * would ask for a second handover and sit waiting on an address nobody was ever given, while
 * the far side reported success. Remembered, it resumes the one already in play.
 *
 * This is a private key on disk, which is not nothing. It is worth it because of what it is:
 * good for one box, for the quarter of an hour the server will hold one, on a device that has
 * no account yet and is minutes from storing a vault in the same place. It is deleted the
 * moment the enrolment finishes or the window closes — see `forgetHandover`, and the callers
 * of it in Handover.tsx, which is every path out of that screen.
 */
export interface HandoverRecord { id: string; pub: Uint8Array; priv: Uint8Array; expiresAt: string }
export const savedHandover = (): HandoverRecord | null => {
  const raw = get(HANDOVER);
  if (!raw) return null;
  const h = JSON.parse(raw);
  // A window that has closed is not a handover, it is litter: drop it rather than hand back
  // a key the caller will only have to check the clock on.
  if (new Date(h.expires_at) <= new Date()) { put(HANDOVER, null); return null; }
  return { id: h.id, pub: unb64(h.pub), priv: unb64(h.priv), expiresAt: h.expires_at };
};
export const saveHandover = (h: HandoverRecord): void => put(HANDOVER, JSON.stringify({
  id: h.id, pub: b64(h.pub), priv: b64(h.priv), expires_at: h.expiresAt,
}));
export const forgetHandover = (): void => put(HANDOVER, null);

/** Leaving this device. The vault goes too — without the passcode it was useless anyway, and
 *  leaving it behind on a shared machine is worse than making someone type their phrase. */
export const forget = (): void => {
  cell.clear();
  platform.forgetSecrets();
  void dropHeld();
};
